import React, { useContext, useEffect, useRef, useState } from 'react';

import { ZeosButton } from '../../components/Shared/ZeosButton';
import { BayouConfig } from '../../configs/BayouConfig';
import { ZeosApiContext } from '../../context/ZeosApiContext';
import {
    isZeosEnvironment, ZeosQaModeWarning
} from '../../services/environment/EnvironmentHelpers';
import { createLogger } from '../../services/Logger';
import { IBayouFormRequest } from '../../services/zeos-api/contracts/Bayou';
import { UapiStatus } from '../../services/zeos-api/enums/Uapi';
import { ZeosSpinner } from '../Shared/ZeosSpinner';

const log = createLogger("BayouModalPoc");

// Bayou needs a few seconds to process customer data after authentication before
// processCustomer will succeed; retry with a fixed debounce instead of failing immediately.
const PROCESS_CUSTOMER_MAX_ATTEMPTS = 5;
const PROCESS_CUSTOMER_RETRY_DELAY_MS = 5000;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface IProps {
  initialize: boolean;
  onClose?: () => void;
  onStatusChange?: (status: UapiStatus) => void;
  zeosUserId: number;
  propertyId: number;
  userEmail: string;
  utilityCode: string;
  showButtons?: boolean;
}

export const BayouEmbed = (props: IProps) => {
  const api = useContext(ZeosApiContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef<boolean>(false);
  const initializedRef = useRef<boolean>(false);

  const [bayouCustomerId, setBayouCustomerId] = useState<number>();
  const [error, setError] = useState<string>("");
  const [events, setEvents] = useState<string[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLinkingUtility, setIsLinkingUtility] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [onboardingToken, setOnboardingToken] = useState<string>("");
  const [status, setStatus] = useState<string>("Ready to initialize");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const showButtons = props.showButtons ?? true;

  useEffect(() => {
    if (scriptLoadedRef.current) {
      return;
    }
    const bayouForm = document.createElement("script");
    bayouForm.src = BayouConfig.scriptUrl;
    bayouForm.async = true;
    bayouForm.onload = () => {
      scriptLoadedRef.current = true;
      setStatus("Bayou successfully loaded");
    };
    bayouForm.onerror = () => {
      log.error("Failed to load Bayou SDK");
      setError("Error connecting to Bayou");
    };
    document.body.appendChild(bayouForm);
    return () => {
      if (document.body.contains(bayouForm)) {
        document.body.removeChild(bayouForm);
      }
    };
  }, []);

  // Auto-trigger form creation and initialization when modal opens
  useEffect(() => {
    if (isModalOpen && !onboardingToken) {
      createBayouForm();
    }
  }, [isModalOpen]);

  // Auto-trigger initialization when onboarding token is available
  useEffect(() => {
    if (onboardingToken && !initializedRef.current && scriptLoadedRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initializeComponent();
      }, 100);
    }
  }, [onboardingToken]);

  const createBayouForm = async () => {
    try {
      setStatus("Connecting to Bayou...");
      setError("");
      const testRequest: IBayouFormRequest = {
        zeosUserId: props.zeosUserId,
        propertyId: props.propertyId,
        emailAddress: props.userEmail,
        utility: props.utilityCode,
      };
      const resp = await api.Bayou.createForm(testRequest);
      if (resp.data) {
        log.info("createBayouForm", resp.data);
        setBayouCustomerId(resp.data.formUid);
        setOnboardingToken(resp.data.onboardingToken);
      } else {
        throw new Error("No data returned from API");
      }
    } catch (err: any) {
      log.error("Error creating form:", err);
      setError(`Error creating form: ${err.message}`);
    }
  };

  const processCustomerWithRetry = async (customerId: number): Promise<boolean> => {
    for (let attempt = 1; attempt <= PROCESS_CUSTOMER_MAX_ATTEMPTS; attempt++) {
      const resp = await api.Bayou.processCustomer(customerId);
      if (resp.zeosStatus === "Success") {
        return true;
      }
      log.info(`processCustomer attempt ${attempt} failed, retrying`, resp.message);
      if (attempt < PROCESS_CUSTOMER_MAX_ATTEMPTS) {
        await sleep(PROCESS_CUSTOMER_RETRY_DELAY_MS);
      }
    }
    log.error("processCustomer failed after all retries", customerId);
    return false;
  };

  const handleEvent = async (event: { type: string }) => {
    log.info("Bayou event received:", event);
    const eventMessage = `Event: ${
      event.type
    } at ${new Date().toLocaleTimeString()}`;
    setEvents((prev) => [...prev, eventMessage]);
    setStatus(`Last event: ${event.type}`);
    log.info("handleEvent", bayouCustomerId);
    if (event.type === "customerHasAuthenticated") {
      if (bayouCustomerId) {
        setIsLinkingUtility(true);
        setError("");
        setSuccessMessage("");
        const processed = await processCustomerWithRetry(bayouCustomerId);
        setIsLinkingUtility(false);
        if (!processed) {
          setError("Error linking utility. Please try again in a moment.");
          props.onStatusChange?.(UapiStatus.ERROR_CHECKING_AUTH);
          return;
        }
      }
      setStatus("Utility successfully linked. Click Done to continue.");
      setSuccessMessage("Utility successfully linked!");
      setIsAuthenticated(true);
      props.onStatusChange?.(UapiStatus.LINKED);
    } else if (event.type === "customerEnteredInvalidCredentials") {
      setStatus("⚠ Invalid credentials entered");
      props.onStatusChange?.(UapiStatus.ERROR_STARTING_AUTH);
    }
  };

  useEffect(() => {
    if (props.initialize && !onboardingToken) {
      if (
        !props.propertyId ||
        !props.zeosUserId ||
        !props.userEmail ||
        !props.utilityCode
      ) {
        setStatus("Waiting for user and utility data...");
        return;
      }
      createBayouForm();
    }
  }, [
    props.initialize,
    onboardingToken,
    props.propertyId,
    props.zeosUserId,
    props.userEmail,
    props.utilityCode,
  ]);

  const initializeComponent = () => {
    if (!window.Bayou) {
      setError("Bayou not loaded yet. Please wait.");
      return;
    }

    if (initializedRef.current) {
      setStatus("Component already initialized. Please refresh page to reset.");
      return;
    }

    if (!containerRef.current) {
      setError("Container element not found");
      return;
    }

    if (!BayouConfig.companyId) {
      setError("Company ID not configured. Please set VITE_BAYOU_COMPANY_ID.");
      return;
    }

    if (!onboardingToken) {
      setError("Onboarding token not available. Please create form first.");
      return;
    }

    try {
      initializedRef.current = true;
      setStatus("Initializing Bayou embedded component...");

      window.Bayou.loadOnboardingForm(
        containerRef.current,
        BayouConfig.companyId,
        onboardingToken,
        handleEvent
      );

      setStatus(
        "Successfully connected to Bayou. Please follow their instructions below."
      );
    } catch (err: any) {
      log.error("Error initializing Bayou component:", err);
      setError(`Error: ${err.message}`);
    }
  };

  const resetState = () => {
    setStatus("Ready to initialize");
    setOnboardingToken("");
    setError("");
    setSuccessMessage("");
    setEvents([]);
    setIsAuthenticated(false);
    setIsLinkingUtility(false);
    initializedRef.current = false;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetState();
    props.onClose?.();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-h-[80vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Link Your Utility</h2>

        {/* {status && (
          <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
            <p>{status}</p>
          </div>
        )} */}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm">
            <p className="text-green-700">{successMessage}</p>
          </div>
        )}

        {isLinkingUtility && (
          <ZeosSpinner message="Finishing up - linking your utility..." />
        )}

        {showButtons && (
          <div className="mt-4 bo flex justify-start gap-2">
            <ZeosButton
              onPress={handleCloseModal}
              label="Cancel"
              type="button"
              mode="action"
              isDisabled={isAuthenticated}
            />
            <ZeosButton
              onPress={handleCloseModal}
              label="Done"
              type="button"
              mode="action"
              isDisabled={!isAuthenticated}
            />
          </div>
        )}

        <div className="mb-4 p-3 bg-white border border-gray-200 rounded">
          <div
            ref={containerRef}
            className="w-full h-[500px] border border-gray-300 rounded overflow-hidden"
          >
            {!initializedRef.current && (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p>Initializing Bayou</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
